import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ImagePicker = ({ onSelect, onClose }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchImages();
    // Prevent background scrolling while modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch media');
      
      const data = await response.json();
      if (data.success && data.result && data.result.images) {
        setImages(data.result.images);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = images.filter(img => 
    (img.filename || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDisplayUrl = (variants) => {
    if (!variants || !Array.isArray(variants) || variants.length === 0) return '';
    const publicVariant = variants.find(v => v.endsWith('/public'));
    return publicVariant || variants[0];
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-6 bg-midnight/95 backdrop-blur-md">
      <div className="bg-charcoal/95 border border-electric-yellow/30 rounded-2xl p-6 w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-dark/50">
          <h3 className="font-clash font-bold text-electric-yellow text-2xl">Select Image from Media Library</h3>
          <button 
            onClick={onClose} 
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-midnight text-soft-gray hover:text-white hover:bg-slate-dark transition-colors border border-slate-dark shadow-sm"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search images by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-midnight/90 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 transition-colors shadow-inner"
          />
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto min-h-0 pr-2 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-electric-yellow text-lg animate-pulse font-medium">Loading your media...</div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-soft-gray">
              <svg className="w-16 h-16 mb-4 opacity-50 text-electric-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-xl font-medium text-white">No images found.</p>
              <p className="text-sm mt-2">Return to the Media Library to upload new images.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 pb-4">
              {filteredImages.map((image) => {
                const url = getDisplayUrl(image.variants);
                return (
                  <div 
                    key={image.id} 
                    onClick={() => onSelect(url)}
                    className="group relative aspect-square rounded-xl overflow-hidden bg-midnight hover:bg-black border border-slate-dark cursor-pointer shadow-md hover:shadow-electric-yellow/20 hover:border-electric-yellow transition-all duration-300"
                  >
                    <img
                      src={url}
                      alt={image.filename}
                      className="w-full h-full object-cover group-hover:scale-105 group-hover:opacity-80 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent flex flex-col justify-end p-3 transform transition-transform duration-300">
                      <p className="text-warm-white text-xs font-medium truncate mb-2 drop-shadow-md">{image.filename}</p>
                      <button className="w-full bg-electric-yellow text-midnight text-sm font-bold py-2 rounded-lg transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 shadow-lg">
                        Select
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImagePicker;
